import { BOND_TYPES_BY_ID } from '@lume/shared';
import { humanizeMinutes } from '@lume/shared/worklist';
import { ArrowRightLeft, Bot } from 'lucide-react';

import type { AwaitingPartyType, SequenceState } from '../../lib/types';
import type { BondType, IntentState, MetaSessionType, OperationModeKind } from '@lume/shared';

/**
 * Badges / indicadores pequenos da Inbox (Brief 2B Tela 1 §6.4 — Linha 3/4).
 *
 * Versões inbox-local pequenas porque as APIs dos componentes reusáveis de
 * Brief 2A (ModeBadge popover, SequenceBadge position/total) não encaixam no
 * dado enriquecido do list item (B3) — aqui só há `state`/`total`, sem
 * position; e ModeBadge é o único reusado direto (vide ConversationCard2B).
 *
 * Consome as classes JÁ materializadas em inbox-2b.css (.meta-badge,
 * .intent-badge, .awaiting-indicator, .transfer-indicator, .urgency-indicator,
 * .badge / .badge--neutral do DS). NÃO cria CSS.
 */

// ─── Intent badge (ADR-108) ────────────────────────────────────────────────
// honest-defer: backend retorna intentState SEMPRE null hoje. O badge existe
// mas só renderiza quando há estado real (ver ConversationCard2B).

const INTENT_LABEL: Record<IntentState, string> = {
  detected: 'Intenção detectada',
  confirmed: 'Intenção confirmada',
  evolving: 'Intenção revisada',
  branched: 'Intenção ramificada',
  completed: 'Intenção resolvida',
  abandoned: 'Intenção abandonada',
  misclassified: 'Intenção reclassificada',
};

const INTENT_VARIANT: Partial<Record<IntentState, string>> = {
  confirmed: 'intent-badge--confirmed',
  detected: 'intent-badge--tentative',
  evolving: 'intent-badge--revised',
  completed: 'intent-badge--resolved',
};

export function IntentBadge({ state }: { state: IntentState }): JSX.Element {
  const variant = INTENT_VARIANT[state] ?? '';
  return (
    <span
      className={['intent-badge', variant].filter(Boolean).join(' ')}
      title={INTENT_LABEL[state]}
    >
      {INTENT_LABEL[state]}
    </span>
  );
}

// ─── Meta session badge (ADR-127) ──────────────────────────────────────────
// honest-defer: sem custo/expira — metaSessionIsExpired não é confiável aqui.

const META_LABEL: Record<MetaSessionType, string> = {
  customer_care: 'Atendimento',
  service: 'Serviço',
  utility: 'Utilidade',
  authentication: 'Autenticação',
  marketing: 'Marketing',
  ctwa: 'CTWA',
};

const META_VARIANT: Record<MetaSessionType, string> = {
  customer_care: 'meta-badge--cc',
  service: 'meta-badge--service',
  utility: 'meta-badge--utility',
  authentication: 'meta-badge--auth',
  marketing: 'meta-badge--marketing',
  ctwa: 'meta-badge--ctwa',
};

export function MetaBadge({ type }: { type: MetaSessionType }): JSX.Element {
  return (
    <span className={`meta-badge ${META_VARIANT[type]}`} title={`Sessão Meta: ${META_LABEL[type]}`}>
      {META_LABEL[type]}
    </span>
  );
}

// ─── Bond badge ─────────────────────────────────────────────────────────────
// Label via catálogo @lume/shared; cor neutra (.badge--neutral do DS).

export function BondBadge({ type }: { type: BondType }): JSX.Element {
  const def = BOND_TYPES_BY_ID[type];
  const label = def?.label ?? type;
  return (
    <span className="badge badge--neutral" title={`Vínculo: ${label}`}>
      {label}
    </span>
  );
}

// ─── Sequence badge (ADR-055) ──────────────────────────────────────────────
// Inbox-local: o list item dá { total, state } sem position — a SequenceBadge
// de 2A exige position/total. Aqui renderizamos "S {total}" com cor por state.

const SEQUENCE_VARIANT: Record<SequenceState, string> = {
  normal: 'sequence-badge--normal',
  warn: 'sequence-badge--warning',
  danger: 'sequence-badge--danger',
};

export function InboxSequenceBadge({
  total,
  state,
}: {
  total: number;
  state: SequenceState;
}): JSX.Element {
  return (
    <span
      role="status"
      aria-label={`Sequência de ${total.toString()} conversas`}
      className={`sequence-badge ${SEQUENCE_VARIANT[state]}`}
    >
      S {total.toString()}
    </span>
  );
}

// ─── Group indicator ────────────────────────────────────────────────────────

export function GroupIndicator({ count }: { count: number }): JSX.Element {
  return (
    <span className="badge badge--neutral" title={`${count.toString()} memberships ativas`}>
      Grupo {count.toString()}
    </span>
  );
}

// ─── Commitment broken ──────────────────────────────────────────────────────

export function CommitmentBrokenBadge(): JSX.Element {
  return (
    <span className="urgency-indicator" title="Compromisso do cliente quebrado">
      Compromisso quebrado
    </span>
  );
}

// ─── Queue-held (S7 PR2 — modo recepcionista S4: IA segurando a fila) ───────
// Rodada 3 Task 4 — o badge era função só de escalationState, e por isso
// afirmava «IA segurando» numa conversa H_a (a IA está PROIBIDA de falar
// ali). Agora é função de (escalationState, operationMode.current): só em
// H_c a IA de fato dirige e segura a fila; qualquer outro modo é escalação
// honesta aguardando humano.

export function QueueHeldBadge({ mode }: { mode: OperationModeKind | '' }): JSX.Element {
  if (mode === 'H_c') {
    return (
      <span className="urgency-indicator" title="A IA está segurando a fila até alguém assumir">
        ⏳ IA segurando
      </span>
    );
  }
  return (
    <span
      className="urgency-indicator urgency-indicator--danger"
      title="Escalada aguardando humano — a IA não está conduzindo esta conversa"
    >
      🔴 Aguardando humano
    </span>
  );
}

// ─── Urgency / P1 (operationalPriority p0/p1 — ADR-146) ─────────────────────

export function UrgencyIndicator(): JSX.Element {
  return (
    <span className="urgency-indicator" title="Alta prioridade">
      Urgente
    </span>
  );
}

// ─── Mode badge (inbox-local — sem popover) ─────────────────────────────────
// honest-defer ausência de popover: o card é read-only; a versão 2A com
// popover/onChange não é necessária na listagem.

const MODE_LABEL: Record<OperationModeKind, string> = {
  H_a: 'Você dirige',
  H_b: 'Você + IA',
  H_c: 'IA dirige',
  H_d: 'Delegado',
};

const MODE_VARIANT: Record<OperationModeKind, string> = {
  H_a: 'mode-badge--you-drive',
  H_b: 'mode-badge--you-assisted',
  H_c: 'mode-badge--ai-drives',
  H_d: 'mode-badge--h-d',
};

export function InboxModeBadge({ mode }: { mode: OperationModeKind }): JSX.Element {
  return (
    <span className={`mode-badge ${MODE_VARIANT[mode]}`} title={MODE_LABEL[mode]}>
      {mode === 'H_c' && <Bot size={10} aria-hidden />}
      {MODE_LABEL[mode]}
    </span>
  );
}

// ─── Transfer indicator ─────────────────────────────────────────────────────
// honest-defer: só o id do remetente está disponível (não o nome) → mostramos
// apenas o tempo, não o id.

export function TransferIndicator({ minutesAgo }: { minutesAgo: number }): JSX.Element {
  return (
    <span className="transfer-indicator" title="Conversa recebida por transferência">
      <ArrowRightLeft size={10} aria-hidden />
      Recebida há {minutesAgo.toString()}min
    </span>
  );
}

// ─── Awaiting indicator (ADR-106) ───────────────────────────────────────────

const AWAITING_LABEL: Record<'customer' | 'operator', string> = {
  customer: 'Aguardando cliente',
  operator: 'Aguardando você',
};

const AWAITING_VARIANT: Record<'customer' | 'operator', string> = {
  customer: 'awaiting-indicator--customer',
  operator: 'awaiting-indicator--operator',
};

export function AwaitingIndicator({
  party,
  durationMinutes,
  overdue,
}: {
  party: Exclude<AwaitingPartyType, 'none'>;
  durationMinutes?: number | null;
  overdue?: boolean;
}): JSX.Element {
  const variant = overdue === true ? 'awaiting-indicator--overdue' : AWAITING_VARIANT[party];
  // `humanizeMinutes` (@lume/shared/worklist) — MESMO formatador da Corrente.
  // Antes daqui saía minuto cru (`70179min` = ~48 dias, medido no app móvel no
  // simulador iOS em 15/Ago/2026, mesma origem de dado): a duração vem em
  // minutos do contrato e era impressa sem escalar unidade. O `> 60` do
  // `ConversationCard2B` é limiar de ESTILO (overdue), nunca de formatação.
  const duration =
    durationMinutes !== null && durationMinutes !== undefined
      ? ` · ${humanizeMinutes(durationMinutes)}`
      : '';
  return (
    <span className={`awaiting-indicator ${variant}`}>
      <span className="awaiting-indicator__dot" aria-hidden />
      {AWAITING_LABEL[party]}
      {duration}
    </span>
  );
}
